import { useCallback, useEffect, useState } from "react";
import {
  Loader2, TrendingUp, Euro, Users2, Repeat, Sparkles, BarChart3, Trash2, ShieldCheck, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";
import {
  fetchBusiness, fetchSubscriptions, fetchAiUsage, fetchPaymentEvents, deleteUser,
} from "@/lib/adminPanel";
import { EMPLOYEE_ROLES, STRATEGIST_PLAN_POOL } from "@/lib/access";

function Loading() {
  return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
}

function KpiCard({ label, value, sub, icon: Icon, testid, estimated }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500 mb-1.5">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        <span className="text-[11px] uppercase tracking-[0.14em]">{label}</span>
      </div>
      <p className="font-display text-2xl font-semibold text-slate-900">
        {value}
        {estimated ? <span className="ml-1 text-[10px] align-super text-amber-600 lowercase tracking-normal">est.</span> : null}
      </p>
      {sub ? <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p> : null}
    </div>
  );
}

export function BusinessPanel() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => { fetchBusiness().then(setData).catch(() => setData(null)); }, []);
  if (!data) return <Loading />;
  const cur = data.currency;
  const money = (v) => `${cur} ${Number(v).toFixed(2)}`;
  return (
    <section data-testid="admin-business" className="space-y-4">
      {!data.hasRealData ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800" data-testid="admin-business-demo-note">
          {t("admin.panel.business.demoNote")}
        </div>
      ) : null}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard label={t("admin.panel.business.mrr")} value={money(data.mrr)} icon={Repeat} estimated={data.mrrEstimated} testid="kpi-mrr" />
        <KpiCard label={t("admin.panel.business.arr")} value={money(data.arr)} icon={TrendingUp} estimated={data.mrrEstimated} testid="kpi-arr" />
        <KpiCard label={t("admin.panel.business.monthlyRevenue")} value={money(data.monthlyRevenue)} icon={Euro} testid="kpi-monthly" />
        <KpiCard label={t("admin.panel.business.yearlyRevenue")} value={money(data.yearlyRevenue)} icon={Euro} testid="kpi-yearly" />
        <KpiCard label={t("admin.panel.business.lifetimeRevenue")} value={money(data.lifetimeRevenue)} icon={Euro} testid="kpi-lifetime" />
        <KpiCard label={t("admin.panel.business.activeSubscribers")} value={data.activeSubscribers} icon={Users2} testid="kpi-active" />
        <KpiCard label={t("admin.panel.business.newUsers")} value={data.newUsers30d} icon={Users2} testid="kpi-new" />
        <KpiCard label={t("admin.panel.business.trials")} value={data.trialSubscribers} icon={Sparkles} testid="kpi-trials" />
        <KpiCard label={t("admin.panel.business.cancelled")} value={data.cancelledSubscriptions} testid="kpi-cancelled" />
        <KpiCard label={t("admin.panel.business.expired")} value={data.expiredSubscriptions} testid="kpi-expired" />
        <KpiCard label={t("admin.panel.business.renewalRate")} value={`${data.renewalRate}%`} estimated={data.ratesEstimated} testid="kpi-renewal" />
        <KpiCard label={t("admin.panel.business.trialConversion")} value={`${data.trialConversionRate}%`} estimated={data.ratesEstimated} testid="kpi-trial-conv" />
      </div>
    </section>
  );
}

export function AiUsagePanel() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => { fetchAiUsage().then(setData).catch(() => setData(null)); }, []);
  if (!data) return <Loading />;
  const roleLabel = (r) => (r ? t(`assistant.modes.${r}.label`) : "—");
  const maxCount = Math.max(1, ...Object.values(data.modeCounts));
  return (
    <section data-testid="admin-ai-usage" className="space-y-4">
      {data.totalEvents === 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800" data-testid="admin-ai-usage-empty">
          {t("admin.panel.aiUsage.empty")}
        </div>
      ) : null}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard label={t("admin.panel.aiUsage.total")} value={data.totalEvents} icon={BarChart3} testid="ai-total" />
        <KpiCard label={t("admin.panel.aiUsage.mostUsed")} value={roleLabel(data.mostUsedRole)} testid="ai-most" />
        <KpiCard label={t("admin.panel.aiUsage.leastUsed")} value={roleLabel(data.leastUsedRole)} testid="ai-least" />
        <KpiCard label={t("admin.panel.aiUsage.standardUsed")} value={data.standardUsed} testid="ai-standard" />
        <KpiCard label={t("admin.panel.aiUsage.advancedUsed")} value={data.advancedUsed} testid="ai-advanced" />
        <KpiCard label={t("admin.panel.aiUsage.premiumZero")} value={data.premiumZeroUsage} testid="ai-premium-zero" />
        <KpiCard label={t("admin.panel.aiUsage.trialUsedAI")} value={data.trialUsersUsingAI} testid="ai-trial-used" />
        <KpiCard label={t("admin.panel.aiUsage.avgAssistant")} value={data.avgPerPlan.assistant} testid="ai-avg-assistant" />
      </div>
      <div className="rounded-2xl border border-border bg-card p-4">
        <h4 className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-3">{t("admin.panel.aiUsage.byRole")}</h4>
        <div className="space-y-2">
          {Object.entries(data.modeCounts).map(([m, c]) => (
            <div key={m} className="flex items-center gap-3" data-testid={`ai-role-bar-${m}`}>
              <span className="w-28 text-sm text-slate-700 shrink-0">{roleLabel(m)}</span>
              <div className="flex-1 h-2.5 rounded-full bg-accent overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${(c / maxCount) * 100}%` }} />
              </div>
              <span className="w-10 text-right font-mono-tab text-sm text-slate-600">{c}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SubscriptionsPanel({ locale, onChanged }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const reload = useCallback(() => {
    fetchSubscriptions().then((d) => setRows(d.subscriptions)).catch(() => setRows([]));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(locale) : "—");
  const onDelete = async (r) => {
    if (!window.confirm(t("admin.panel.subs.confirmDelete", { name: r.alias || r.name }))) return;
    setBusy(r.id);
    try {
      await deleteUser(r.id);
      toast.success(t("admin.panel.subs.deleted"));
      reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  if (!rows) return <Loading />;
  const filtered = rows.filter((r) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [r.name, r.alias, r.recoveryEmail].some((v) => (v || "").toLowerCase().includes(s));
  });

  const credits = (r) => {
    if (EMPLOYEE_ROLES.includes(r.role) || r.assistantCreditsRemaining === "unlimited") return "∞";
    if (r.plan === "strategist") {
      const v = typeof r.strategistCreditsRemaining === "number" ? r.strategistCreditsRemaining : STRATEGIST_PLAN_POOL;
      return `${v} / ${STRATEGIST_PLAN_POOL}`;
    }
    return `${r.assistantCreditsRemaining} / ${r.strategistCreditsRemaining}`;
  };

  return (
    <section data-testid="admin-subscriptions" className="space-y-3">
      <div className="relative max-w-sm">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.panel.subs.search")} className="pl-9 rounded-xl h-10" data-testid="admin-subs-search" />
      </div>
      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 border-b border-border">
              <th className="px-4 py-3 font-medium">{t("admin.panel.subs.user")}</th>
              <th className="px-4 py-3 font-medium">{t("admin.col.plan")}</th>
              <th className="px-4 py-3 font-medium">{t("admin.col.status")}</th>
              <th className="px-4 py-3 font-medium">{t("admin.panel.subs.renewal")}</th>
              <th className="px-4 py-3 font-medium">{t("admin.panel.subs.credits")}</th>
              <th className="px-4 py-3 font-medium">{t("admin.panel.subs.data")}</th>
              <th className="px-4 py-3 font-medium text-right">{t("admin.col.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-border/60 last:border-0" data-testid={`admin-sub-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="text-slate-800">{r.name || r.alias}</div>
                  <div className="text-[11px] text-slate-400">{r.recoveryEmail}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  <div>{t(`settings.plans.${r.plan}`)}</div>
                  {r.activatedBy ? (
                    <div className="text-[10px] uppercase tracking-wide text-slate-400" data-testid={`admin-sub-source-${r.id}`}>
                      {r.activatedBy}{r.interval ? ` · ${r.interval}` : ""}
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <span className={["inline-flex rounded-full text-[11px] font-medium px-2 py-0.5", r.status === "active" ? "bg-emerald-100 text-emerald-700" : r.status === "trialing" ? "bg-sky-100 text-sky-700" : r.status === "past_due" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"].join(" ")}>
                    {t(`palvelumme.statuses.${r.status}`) || r.status}
                  </span>
                  {r.cancelAtPeriodEnd ? (
                    <div className="text-[10px] text-amber-600 mt-0.5" data-testid={`admin-sub-cancel-${r.id}`}>ends at period end</div>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs font-mono-tab">{fmt(r.renewalDate || r.currentPeriodEnd)}</td>
                <td className="px-4 py-3 text-slate-600 font-mono-tab">{credits(r)}</td>
                <td className="px-4 py-3">{r.hasData ? <span className="text-[11px] text-slate-500">{t("admin.panel.subs.hasData")}</span> : <span className="text-[11px] text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-right">
                  {r.protected ? (
                    <span className="inline-flex items-center gap-1 text-amber-700 text-xs"><ShieldCheck className="h-3.5 w-3.5" />{t("admin.protected")}</span>
                  ) : r.deletion.deletable ? (
                    <Button size="sm" variant="outline" className="h-8 rounded-full text-xs border-rose-200 text-rose-600 hover:bg-rose-50" disabled={busy === r.id} onClick={() => onDelete(r)} data-testid={`admin-delete-user-${r.id}`}>
                      <Trash2 className="h-3 w-3 mr-1" />{t("admin.panel.subs.delete")}
                    </Button>
                  ) : (
                    <span className="text-[11px] text-slate-400" data-testid={`admin-delete-blocked-${r.id}`} title={r.deletion.blockedUntil || ""}>
                      {t(`admin.panel.subs.block.${r.deletion.reason}`)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function LogsPanel({ locale }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  useEffect(() => { fetchPaymentEvents().then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return <Loading />;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(locale) : "—");
  const typeColor = {
    purchase: "bg-emerald-100 text-emerald-700", trial_start: "bg-sky-100 text-sky-700",
    failed_payment: "bg-rose-100 text-rose-700", expired: "bg-slate-200 text-slate-600",
    initiated: "bg-amber-100 text-amber-700",
  };
  return (
    <section data-testid="admin-logs" className="rounded-2xl border border-border bg-card overflow-x-auto">
      {rows.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-slate-400" data-testid="admin-logs-empty">{t("admin.panel.logs.empty")}</p>
      ) : (
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 border-b border-border">
              <th className="px-4 py-3 font-medium">{t("admin.panel.logs.event")}</th>
              <th className="px-4 py-3 font-medium">{t("admin.col.plan")}</th>
              <th className="px-4 py-3 font-medium">{t("admin.panel.logs.amount")}</th>
              <th className="px-4 py-3 font-medium">{t("admin.panel.subs.user")}</th>
              <th className="px-4 py-3 font-medium">{t("admin.panel.logs.ref")}</th>
              <th className="px-4 py-3 font-medium">{t("admin.col.created")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60 last:border-0" data-testid={`admin-log-row-${r.id}`}>
                <td className="px-4 py-3">
                  <span className={["inline-flex rounded-full text-[11px] font-medium px-2 py-0.5", typeColor[r.type] || "bg-slate-100 text-slate-600"].join(" ")}>
                    {t(`admin.panel.logs.types.${r.type}`) || r.type}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-700">{r.plan ? t(`settings.plans.${r.plan}`) : "—"}</td>
                <td className="px-4 py-3 font-mono-tab text-slate-700">{r.amount != null ? `${r.currency} ${Number(r.amount).toFixed(2)}` : "—"}</td>
                <td className="px-4 py-3 text-slate-600">{r.user}</td>
                <td className="px-4 py-3 font-mono-tab text-xs text-slate-400">{r.sessionRef}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{fmt(r.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
